import clsx from 'clsx';
import React, { useCallback, useRef, useState } from 'react';

import Button from '@components/Button';
import IconWave from '@components/Images/IconWave';
import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, ContentType, click } from '@tracking';

import ToolTip from './ToolTip';

interface ListenButtonProps {
  isAudioActive: boolean;
  setIsAudioActive: (isAudioActive: boolean) => void;
  contentType: ContentType;
}

export const AUDIO_LISTEN_ONBOARDING_COOKIE = 'audio-listen-onboarding-dismissed';
export const TOOLTIP_BUTTON_TEXT = 'Upgrade today';
export const TOOLTIP_BUTTON_TEXT_NON_MONTHLY = 'Got it';
const AUDIO_BUTTON_TEXT = 'Listen';

const ListenButton = ({ isAudioActive, setIsAudioActive, contentType }: ListenButtonProps) => {
  const [listenOnboardingOpen, setListenOnboardingOpen] = useState(false);
  const { trackEvent } = useEventTracking();
  const audioButtonTouchPointerRef = useRef(false);
  const { currentUser, isLoadingCurrentUser } = useCurrentUser();

  const handleAudioButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!currentUser?.subscription?.is_monthly && !isLoadingCurrentUser) {
        setIsAudioActive(!isAudioActive);
      }
      trackEvent(
        click({
          clickTarget: ClickTarget.AudioButton,
          clickText: `${AUDIO_BUTTON_TEXT}: ${isAudioActive ? 'opened' : 'closed'}`,
        })
      );
      // Android Chrome keeps :focus after tap; blur so the button returns to inactive styles without sticky focus/highlight.
      if (audioButtonTouchPointerRef.current) {
        audioButtonTouchPointerRef.current = false;
        queueMicrotask(() => event.currentTarget.blur());
      }
    },
    [
      isAudioActive,
      setIsAudioActive,
      trackEvent,
      currentUser?.subscription?.is_monthly,
      isLoadingCurrentUser,
    ]
  );

  if (isLoadingCurrentUser) return null;

  return (
    <ToolTip
      className="relative inline-flex print:hidden"
      isSubscriber={currentUser?.is_subscriber}
      isMonthlySubscriber={currentUser?.subscription?.is_monthly}
      onListenOnboardingOpenChange={setListenOnboardingOpen}
      contentType={contentType}
    >
      <Button
        as="button"
        className={clsx(
          'audio-button group flex touch-manipulation items-center gap-12 rounded-20 border border-solid border-grayscale-gray bg-white px-16 py-8 font-suisse-intl-mono text-button-xs text-grayscale-gray outline-none ring-0 transition-[color,background-color,box-shadow,border-color] duration-150 ease-in-out [-webkit-tap-highlight-color:transparent] [transform:translateZ(0)] [@media(hover:hover)_and_(pointer:fine)]:hover:border-white [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary-magenta',
          {
            '!border-white !bg-primary-magenta !text-white': isAudioActive,
          }
        )}
        onPointerDown={(e) => {
          audioButtonTouchPointerRef.current = e.pointerType === 'touch' || e.pointerType === 'pen';
        }}
        onClick={handleAudioButtonClick}
      >
        <IconWave
          active={isAudioActive || listenOnboardingOpen}
          className={clsx(
            'text-primary-magenta [@media(hover:hover)_and_(pointer:fine)]:group-hover:text-white',
            {
              '!text-white': isAudioActive,
            }
          )}
        />
        <span className="[@media(hover:hover)_and_(pointer:fine)]:group-hover:text-white">
          {AUDIO_BUTTON_TEXT}
        </span>
      </Button>
    </ToolTip>
  );
};

export default ListenButton;
